import React from "react";

import { AddQuestionTile } from "./AddQuestion/AddQuestionTile";
import { QuestionTile } from "./Question/QuestionTile";
import { useSurvey } from "./store/reducer";
import { QuestionOptionType } from "survey";
import { RatingQuestionFormValues } from "./Question/forms/RatingQuestionForm";
import { TextQuestionFormValues } from "./Question/forms/TextQuestionForm";
import { SelectQuestionFormValues } from "./Question/forms/SelectQuestionForm";
import { moveItem, sameOrder, savedQuestionIds } from "./questionOrder";

interface ShSurveyCreatorProps {
  options: QuestionOptionType[];
  onSubmit: (
    data: RatingQuestionFormValues | TextQuestionFormValues | SelectQuestionFormValues,
  ) => Promise<number | void>;
  onDeleteQuestion: (id: number) => Promise<void>;
  /**
   * Saves the order of the saved questions right after a move; throw to undo the move. Without it (exams) the order
   * travels with each question's own save, so a move marks the moved questions as unsaved.
   */
  onReorder?: (questionIds: number[]) => Promise<void>;
}
export const ShSurveyCreator = ({ options, onSubmit, onDeleteQuestion, onReorder }: ShSurveyCreatorProps) => {
  const { state, dispatch } = useSurvey();
  const [reordering, setReordering] = React.useState(false);

  const { questions, openQuestionIdx } = state;
  const setOpenQuestionIdx = (idx: number | null) => dispatch({ type: "SET_OPEN_QUESTION_IDX", payload: idx });
  const deleteQuestion = (idx: number) => dispatch({ type: "DELETE_QUESTION", payload: idx });
  const duplicateQuestion = (idx: number) => dispatch({ type: "DUPLICATE_QUESTION", payload: idx });
  const addQuestion = (type: QuestionOptionType) => dispatch({ type: "ADD_QUESTION", payload: type });
  const openPreview = (idx: number) => dispatch({ type: "SET_PREVIEW_QUESTION_IDX", payload: idx });

  const moveQuestion = async (idx: number, delta: -1 | 1) => {
    const moved = moveItem(questions, idx, delta);
    if (!moved || reordering) return;
    const move = (from: number, d: -1 | 1) =>
      dispatch({ type: d < 0 ? "MOVE_QUESTTION_UP" : "MOVE_QUESTTION_DOWN", payload: from });
    move(idx, delta);
    if (!onReorder) return;

    const ids = savedQuestionIds(moved);
    // moving an unsaved question past others leaves the saved order as it was
    if (sameOrder(ids, savedQuestionIds(questions))) return;
    setReordering(true);
    try {
      await onReorder(ids);
    } catch {
      move(idx + delta, delta < 0 ? 1 : -1);
    } finally {
      setReordering(false);
    }
  };

  const onDeleteHandler = async (questionId: number | undefined, idx: number) => {
    if (questionId) await onDeleteQuestion(questionId);
    deleteQuestion(idx);
    openPreview(0);
  };

  return (
    <div className="flex flex-col gap-4">
      {questions.map((question) => (
        <QuestionTile
          key={question.key + question.id} // Id is added to key to force re-render when question is saved for the first time (created). Id changes in the store and we need to re-render the component.
          onSubmit={onSubmit}
          question={question}
          isOpen={question.index === openQuestionIdx}
          onClose={() => setOpenQuestionIdx(null)}
          onOpen={() => {
            setOpenQuestionIdx(question.index);
            openPreview(question.index);
          }}
          onDelete={() => onDeleteHandler(question.id, question.index)}
          onDuplicate={() => duplicateQuestion(question.index)}
          onMoveUp={() => moveQuestion(question.index, -1)}
          onMoveDown={() => moveQuestion(question.index, 1)}
          moving={reordering}
          orderSavedSeparately={!!onReorder}
        />
      ))}
      <AddQuestionTile
        options={options}
        onClick={(x) => {
          addQuestion(x);
          openPreview(questions.length);
        }}
        // @ts-ignore
        onSubmit={onSubmit}
      />
    </div>
  );
};
